import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customerAuthService } from '@/services/customer-auth.service';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import type { CustomerMeProfile, UpdateMyContactPayload } from '@/types/customer-auth.types';

export const MY_ACCOUNT_QUERY_KEY = ['customer-auth', 'me-profile'] as const;

/**
 * User Profile P2 — the signed-in contact's account, read and written through
 * the self-scoped `/customer-portal/auth/me` (no id anywhere). Every success
 * syncs the display-safe keys back into the auth store so the header and the
 * forced-change guard never disagree with the page.
 */
export function useMyAccount() {
  const client = useQueryClient();
  const updateUser = useCustomerAuthStore((s) => s.updateUser);

  const query = useQuery({
    queryKey: MY_ACCOUNT_QUERY_KEY,
    queryFn: () => customerAuthService.getMeProfile(),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data) syncStore(query.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  function syncStore(me: CustomerMeProfile) {
    updateUser({
      firstName: me.firstName,
      lastName: me.lastName,
      email: me.email,
      username: me.username,
      mustChangePassword: me.mustChangePassword,
    });
  }

  const save = useMutation({
    mutationFn: (payload: UpdateMyContactPayload) => customerAuthService.updateMe(payload),
    onSuccess: (me) => {
      client.setQueryData(MY_ACCOUNT_QUERY_KEY, me);
      syncStore(me);
    },
  });

  return { query, save };
}
